'use client'

import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'

interface MethodData {
	anchor_title?: string
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	text_below?: {
		text?: string
	}
	background?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string
					text: string
				}
			}[]
		}
	}[]
	button?: {
		href: string
		label: string
	}
}

export const fields = [
	'anchor_title',
	'heading.title',
	'text.text',
	'text_below.text',
	'background.alt',
	'background.image_file.image.id',
	'background.image_file.image.filename_download',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'button.href',
	'button.label',
] as const

interface MethodProps {
	data?: MethodData
}

export default function Method({ data }: MethodProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionIdBase = data?.anchor_title || 'Method'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	const items = data?.content_collection || []
	const itemCount = items.length

	return (
		<section
			id={sectionId}
			data-name={data?.anchor_title || 'Method'}
			ref={ref}
			className='Method relative w-full pb-12 md:pb-20 overflow-hidden md:bg-transparent bg-[#60676A]'
		>
			{/* Background Image */}
			<div className='absolute inset-0 hidden md:block'>
				{data?.background?.image_file?.image?.id && (
					<Image
						src={getReadableImageUrl(
							data.background.image_file.image.id,
							data.background.image_file.image.filename_disk || ''
						)}
						alt={data.background.alt || ''}
						fill
						className='object-cover object-center'
					/>
				)}
			</div>

			{/* Content Container */}
			<div className='container relative z-10'>
				{/* Header Section */}
				<div className='relative'>
					<div className='absolute right-full top-0 w-screen h-full bg-[rgb(255,255,255,0.7)]' />

					<FadeIn
						isVisible={isVisible}
						delay={0}
						direction='right'
						className='relative w-full md:w-1/2 bg-[rgb(255,255,255,0.7)] py-12 md:pr-12 xl:pr-20 xl:py-20 md:rounded-tr-lg md:rounded-br-lg'
					>
						<h2>{data?.heading?.title || ''}</h2>

						<div
							className='grid gap-4'
							dangerouslySetInnerHTML={sanitizeHTML(
								(data?.text?.text || '').replace(
									/<p>/g,
									'<p class="xl:text-xl">'
								)
							)}
						/>
					</FadeIn>

					<div className='absolute left-full top-0 w-screen h-full bg-[rgb(255,255,255,0.7)] md:hidden' />
				</div>
			</div>

			{/* Mobile Image */}
			<FadeIn isVisible={isVisible} delay={100} className='md:hidden w-full'>
				{data?.background?.image_file?.image?.id && (
					<Image
						src={getReadableImageUrl(
							data.background.image_file.image.id,
							data.background.image_file.image.filename_disk || ''
						)}
						alt={data.background.alt || ''}
						width={768}
						height={400}
						className='w-full h-auto'
					/>
				)}
			</FadeIn>

			<div className='container relative z-10'>
				{/* Method Grid */}
				<div
					className={`MethodGrid gap-8 mt-12 mb-8 ${(() => {
						// 2 or fewer - regular 6 column grid
						if (itemCount <= 2) {
							if (itemCount === 1) {
								return 'grid md:gap-1 md:grid-cols-2 2xl:grid-cols-2'
							} else {
								return 'grid md:grid-cols-3 2xl:grid-cols-5'
							}
						}

						// 3-4 elements - stretch to full width
						if (itemCount >= 3 && itemCount <= 4) {
							return 'grid md:grid-cols-3 2xl:grid-cols-5 lg:flex'
						}

						// 5-6 elements - regular grid
						if (itemCount >= 5 && itemCount <= 6) {
							return 'grid md:grid-cols-3 2xl:grid-cols-5'
						}

						// 7+ elements - special layout
						return 'grid md:grid-cols-3 2xl:grid-cols-5'
					})()}`}
				>
					{(() => {
						// Special layout only for 7+ elements
						if (itemCount >= 7) {
							const firstRowCount = Math.ceil(itemCount / 2)

							return (
								<>
									{/* Mobile version - regular list */}
									<div className='2xl:hidden contents'>
										{items.map((item, index) => (
											<FadeIn
												key={`method-mobile-${item.id}-${index}`}
												isVisible={isVisible}
												delay={200 + index * 100}
												direction='up'
												className='flex-1 px-6 py-7 bg-black/50 backdrop-blur-[13.30px] rounded-lg max-md:grid max-md:grid-cols-[2.4rem_1fr] max-md:gap-3'
											>
												<div className='flex items-center gap-3 md:mb-6'>
													<div className='size-9 bg-[#ffffff] rounded-[3.125rem] flex items-center justify-center max-md:h-full'>
														<span className='text-white text-2xl uppercase'>
															{index + 1}
														</span>
													</div>
												</div>
												<div className='text-white'>
													{item?.site_content_collections_id?.collection?.find(
														c => c.item.title
													)?.item.title && (
														<div
															className='text-3xl font-bold mb-4'
															dangerouslySetInnerHTML={sanitizeHTML(
																item.site_content_collections_id.collection.find(
																	c => c.item.title
																)?.item.title || ''
															)}
														/>
													)}
													{item?.site_content_collections_id?.collection?.find(
														c => c.item.text
													)?.item.text && (
														<div
															className='text-base [&_p]:pb-4 [&_p:last-child]:pb-0'
															dangerouslySetInnerHTML={sanitizeHTML(
																item.site_content_collections_id.collection.find(
																	c => c.item.text
																)?.item.text || ''
															)}
														/>
													)}
												</div>
											</FadeIn>
										))}
									</div>

									{/* Desktop version - special layout for 7+ elements */}
									<div className='hidden 2xl:contents'>
										{/* First row */}
										<div className='col-span-full grid grid-cols-6 gap-8'>
											{items.slice(0, firstRowCount).map((item, index) => (
												<FadeIn
													key={`method-row1-${item.id}-${index}`}
													isVisible={isVisible}
													delay={200 + index * 100}
													direction='up'
													className='flex-1 px-6 py-7 bg-black/30 backdrop-blur-[13.30px] rounded-lg col-span-1'
												>
													<div className='flex items-center gap-3 mb-6'>
														<div className='size-9 bg-[#7f8674] rounded-tl-md rounded-br-md flex items-center justify-center'>
															<span className='text-white text-2xl uppercase'>
																{index + 1}
															</span>
														</div>
													</div>
													<div className='text-white'>
														{item?.site_content_collections_id?.collection?.find(
															c => c.item.title
														)?.item.title && (
															<div
																className='text-3xl font-bold mb-4'
																dangerouslySetInnerHTML={sanitizeHTML(
																	item.site_content_collections_id.collection.find(
																		c => c.item.title
																	)?.item.title || ''
																)}
															/>
														)}
														{item?.site_content_collections_id?.collection?.find(
															c => c.item.text
														)?.item.text && (
															<div
																className='text-base [&_p]:pb-4 [&_p:last-child]:pb-0'
																dangerouslySetInnerHTML={sanitizeHTML(
																	item.site_content_collections_id.collection.find(
																		c => c.item.text
																	)?.item.text || ''
																)}
															/>
														)}
													</div>
												</FadeIn>
											))}
										</div>

										{/* Second row */}
										<div className='col-span-full grid grid-cols-6 gap-8'>
											{items.slice(firstRowCount).map((item, index) => (
												<FadeIn
													key={`method-row2-${item.id}-${index}`}
													isVisible={isVisible}
													delay={200 + (firstRowCount + index) * 100}
													direction='up'
													className='flex-1 px-6 py-7 bg-black/30 backdrop-blur-[13.30px] rounded-lg col-span-1'
												>
													<div className='flex items-center gap-3 mb-6'>
														<div className='size-9 bg-[#7f8674] rounded-tl-md rounded-br-md flex items-center justify-center'>
															<span className='text-white text-2xl uppercase'>
																{firstRowCount + index + 1}
															</span>
														</div>
													</div>
													<div className='text-white'>
														{item?.site_content_collections_id?.collection?.find(
															c => c.item.title
														)?.item.title && (
															<div
																className='text-3xl font-bold mb-4'
																dangerouslySetInnerHTML={sanitizeHTML(
																	item.site_content_collections_id.collection.find(
																		c => c.item.title
																	)?.item.title || ''
																)}
															/>
														)}
														{item?.site_content_collections_id?.collection?.find(
															c => c.item.text
														)?.item.text && (
															<div
																className='text-base [&_p]:pb-4 [&_p:last-child]:pb-0'
																dangerouslySetInnerHTML={sanitizeHTML(
																	item.site_content_collections_id.collection.find(
																		c => c.item.text
																	)?.item.text || ''
																)}
															/>
														)}
													</div>
												</FadeIn>
											))}
										</div>
									</div>
								</>
							)
						}

						// Regular layout for 3-6 elements and 2 or fewer
						return items.map((item, index) => (
							<FadeIn
								key={`method-${item.id}-${index}`}
								isVisible={isVisible}
								delay={200 + index * 100}
								direction='up'
								className='flex-1 px-6 py-7 bg-black/50 backdrop-blur-[13.30px] rounded-lg max-md:grid max-md:grid-cols-[2.4rem_1fr] max-md:gap-3'
							>
								<div className='flex md:items-center gap-3 md:mb-6'>
									<div className='size-9 bg-[#ffffff] rounded-[3.125rem] flex items-center justify-center max-md:mt-2'>
										<span className='text-2xl uppercase'>{index + 1}</span>
									</div>
								</div>
								<div className='text-white'>
									{item?.site_content_collections_id?.collection?.find(
										c => c.item.title
									)?.item.title && (
										<div
											className='text-3xl font-bold mb-4'
											dangerouslySetInnerHTML={sanitizeHTML(
												item.site_content_collections_id.collection.find(
													c => c.item.title
												)?.item.title || ''
											)}
										/>
									)}
									{item?.site_content_collections_id?.collection?.find(
										c => c.item.text
									)?.item.text && (
										<div
											className='text-base [&_p]:pb-4 [&_p:last-child]:pb-0'
											dangerouslySetInnerHTML={sanitizeHTML(
												item.site_content_collections_id.collection.find(
													c => c.item.text
												)?.item.text || ''
											)}
										/>
									)}
								</div>
							</FadeIn>
						))
					})()}
				</div>

				{/* Text Below Card */}
				{data?.text_below?.text && (
					<FadeIn
						isVisible={isVisible}
						delay={200 + itemCount * 100}
						className='w-full mb-12'
					>
						<div className='w-full px-6 py-7 bg-black/50 backdrop-blur-[13.30px] rounded-lg'>
							<div
								className='text-white text-lg md:text-center grid gap-4'
								dangerouslySetInnerHTML={sanitizeHTML(data.text_below.text)}
							/>
						</div>
					</FadeIn>
				)}

				{/* CTA Button */}
				{data?.button && (
					<FadeIn
						isVisible={isVisible}
						delay={200 + itemCount * 100 + (data?.text_below?.text ? 100 : 0)}
						className='max-md:w-full md:flex md:justify-end'
					>
						<Link
							href={data.button?.href || '/'}
							className='btn'
							onClick={e => {
								if (data.button?.href) {
									e.preventDefault()
									window.location.href = data.button.href
								}
							}}
						>
							{data.button.label}
						</Link>
					</FadeIn>
				)}
			</div>
		</section>
	)
}

